// NPM Dependencies
import React from 'react';
import { useDispatch } from 'react-redux';

// Styles
import { FlexDiv, GenericDiv, TooltipContainer } from 'commonStyles';
import { ApprovalIcons } from './listingStyles';

// Components
import ApprovalButtons from './ApprovalButtons';
import ToolTip from './PortaledTooltip';

// Constants
import { ADOBE_EVENT_VALUES, MRB_CONSTANTS } from 'AppConstants';
import {
	ACTION_BUTTON_CONFIG,
	ACTION_KEYS,
	EXPIRED_REQUEST_LABEL
} from '../../config';

// Redux Actions
import { modifyBooking, downloadVoucher } from 'reduxStore/actions/manageRequestBookingActions';

// Utils
import { pushManageBookingsClick } from 'UtilityComponents/manageRequestBookingUtils';
import { noop } from 'UtilityComponents/Util';

const MANAGE_BOOKING_EVENTS = ADOBE_EVENT_VALUES.MANAGE_BOOKING || {};

const isModifyDisabled = (button, bookingStatus) =>
	button.key === ACTION_KEYS.MODIFY && bookingStatus?.toLowerCase() === 'cancelled';

const APPROVAL_TOOLTIP_PROPS = {
	transform: 'translateX(-50%)',
	top_arr: true,
	posLeft: '8px',
	posTop: '40px',
	bg_color: '#000',
	color: '#fff',
	boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
	padding: '10px 12px',
	$width: '132px'
};

const renderTooltipIcon = (iconKey, label) => (
	<TooltipContainer position='relative' valign='middle'>
		<ApprovalIcons icon={iconKey} />
		<ToolTip {...APPROVAL_TOOLTIP_PROPS}>{label}</ToolTip>
	</TooltipContainer>
);

const Action = ({ url, type, bookingId, bookingStatus, requisitionId, workflowId, disableApprovalKeys }) => {
	const dispatch = useDispatch();
	const isPending = type === MRB_CONSTANTS.PENDING_REQUEST_TYPE;

	if (type === MRB_CONSTANTS.EXPIRED_REQUEST_TYPE) {
		return (
			<GenericDiv $bg='#f7f7f7' padding='4px 10px' bdrRad='16px' left_M='-32'>
				{EXPIRED_REQUEST_LABEL}
			</GenericDiv>
		);
	}

	const handleViewDetails = () => {
		pushManageBookingsClick(MANAGE_BOOKING_EVENTS.PENDING_REQUEST_VIEW_DETAILS);
		if (url) window.open(url);
	};

	const handleModify = () => {
		pushManageBookingsClick(MANAGE_BOOKING_EVENTS.ALL_BOOKINGS_MODIFY);
		dispatch(modifyBooking(bookingId));
	};

	const handleDownload = () => {
		pushManageBookingsClick(MANAGE_BOOKING_EVENTS.ALL_BOOKINGS_DOWNLOAD);
		dispatch(downloadVoucher({ bookingId, invoiceType: MRB_CONSTANTS.INVOICE_TYPE.E_VOUCHER }));
	};

	const handlers = {
		[ACTION_KEYS.VIEW_DETAILS]: isPending ? handleViewDetails : handleModify,
		[ACTION_KEYS.MODIFY]: handleModify,
		[ACTION_KEYS.DOWNLOAD]: handleDownload
	};

	return (
		<FlexDiv justify={disableApprovalKeys ? 'center' : 'flex-start'} $width='auto' align='center' $gap='8px'>
			{ACTION_BUTTON_CONFIG[type].map((button) => {
				const disabled = isModifyDisabled(button, bookingStatus);
				const icon = disabled ? `disabled_${button.icon}` : button.icon;
				const onClick = disabled ? noop : (handlers[button.key] || noop);

				return (
					<TooltipContainer key={button.key} position='relative' valign='middle'>
						<ApprovalIcons icon={icon} onClick={onClick} />
						{!disabled && (
							<ToolTip {...APPROVAL_TOOLTIP_PROPS}>{button.label}</ToolTip>
						)}
					</TooltipContainer>
				);
			})}
			{isPending && !disableApprovalKeys && (
				<ApprovalButtons
					appearance={{
						type: 'icon',
						approveIcon: renderTooltipIcon('approve', 'Approve Request'),
						rejectIcon: renderTooltipIcon('reject', 'Reject Request')
					}}
					approveIcon={renderTooltipIcon('approve', 'Approve Request')}
					rejectIcon={renderTooltipIcon('reject', 'Reject Request')}
					pageType='ManageBookings'
					requisitionId={requisitionId}
					workflowId={workflowId}
				/>
			)}
		</FlexDiv>
	);
};

export default Action;
